<script setup>
import { BI_DEFAULT_PALETTE_COLORS } from '~/bi/constants/bi-constants';
import { getIsUniqueValidator } from '~/common/composables/custom-validators.js';

const props = defineProps({
  columnDetails: {
    type: Object,
    default: () => {},
  },
  allowedTypes: {
    type: Array,
    default: () => [],
  },
  defaultOptions: {
    type: Array,
    default: () => [],
  },
  existingColumns: {
    type: Array,
    default: () => [],
  },
});

const emits = defineEmits(['close', 'save']);

const $t = inject('$t');

const form$ = ref(null);
const form = ref({
  name: null,
  type: null,
  options: [],
});

const column_types = computed(() => {
  return [
    {
      label: $t('Text'),
      value: 'text',
    },
    {
      label: $t('Number'),
      value: 'numeric',
    },
    {
      label: $t('Date'),
      value: 'date',
    },
    {
      label: `${$t('Date')} & ${$t('Time')}`,
      value: 'timestamp',
    },
    {
      label: $t('Dropdown'),
      value: 'dropdown',
    },
    {
      label: $t('Checkbox'),
      value: 'checkbox',
    },
  ];
});

function getColumnTypes() {
  return column_types.value.filter(type => props.allowedTypes.length === 0 || props.allowedTypes.includes(type.value));
}
function handleLoadOptions(value) {
  form.value.type = value;
  if (form.value.type === 'dropdown') {
    form.value.options = [];
    form.value.options = props.defaultOptions;
    form$.value.elements$.options.update(form.value.options);
  }
}
async function handleSave() {
  const form_data = form$.value.data;
  const column_details = {
    title: form_data.name,
    data: props.columnDetails?.data || form_data.name,
    type: form_data.type,
  };
  if (column_details.type === 'dropdown') {
    column_details.options = form_data.options;
    column_details.allow_multi_select = form_data.allow_multi_select;
  }
  if (!props.columnDetails) {
    column_details.width = 200;
  }
  emits('save', column_details);
}
const focusElement = () => form$.value.elements$.name.input.focus();
async function onFormMounted() {
  const type = props.columnDetails?.type || 'text';
  form.value = {
    name: props.columnDetails?.title || '',
    type,
    options: props.columnDetails?.options || [],
    allow_multi_select: props.columnDetails?.allow_multi_select || false,
    allowed_types: props.allowedTypes,
  };
  await form$.value.load(form.value, true);
};
</script>

<template>
  <hawk-modal-container id="bi-edit-column-popup" content_class="rounded-lg w-[700px]" :options="{ 'focus-trap': { allowOutsideClick: true, onPostActivate: focusElement } }">
    <Vueform
      ref="form$"
      v-model="form"
      :display-errors="false"
      size="sm"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 12, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
      :messages="{ required: $t('This field is required') }"
      @submit="handleSave"
      @mounted="onFormMounted"
    >
      <div class="col-span-12">
        <hawk-modal-header @close="emits('close')">
          <template #title>
            {{ props.columnDetails?.data ? $t('Edit column') : $t('Add column') }}
          </template>
        </hawk-modal-header>
        <hawk-modal-content>
          <TextElement
            name="name"
            :label="$t('Name')"
            :rules="[
              'required',
              'max:48',
              getIsUniqueValidator(props.existingColumns, $t('Column name already exists')),
            ]"
            :placeholder="$t('Enter name')"
            autocomplete="off"
            class="mb-6"
          />
          <SelectElement
            name="type"
            :label="$t('Type')"
            :items="getColumnTypes()"
            :native="false"
            :search="true"
            :can-deselect="false"
            :can-clear="false"
            append-to="#bi-edit-column-popup"
            rules="required"
            class="mb-6"
            @select="handleLoadOptions"
          />
          <CheckboxElement
            name="allow_multi_select"
            :label="$t('Allow multi select')"
            :conditions="[
              ['type', 'in', ['dropdown']],
            ]"
            class="mb-6"
          />
          <ListElement
            name="options"
            :label="$t('Options')"
            :default="[]"
            :rules="[
              'required',
              'distinct',
            ]"
            :remove-classes="{
              ListElement: {
                add: ['form-bg-primary', 'form-border-color-primary', 'form-color-on-primary', 'form-border-width-btn', 'hover:scale-105', 'focus:form-ring'],
              },
            }"
            :add-classes="{
              ListElement: {
                add: ['text-gray-600', 'font-semibold', 'text-sm'],
                handle: ['left-3'],
              },
            }"
            :conditions="[
              ['type', 'in', ['dropdown']],
            ]"
            :add-text="`+ ${$t('Add Option')}`"
            :controls="{ add: true, remove: false, sort: true }"
            :sort="true"
            class="mb-6"
          >
            <template #default="{ index }">
              <div class="col-span-12">
                <div class="flex items-center gap-3">
                  <TextElement
                    :name="index"
                    rules="required"
                    class="w-full"
                    autocomplete="off"
                  />
                  <div class="cursor-pointer" @click="form$.elements$.options.remove(index)">
                    <IconHawkTrashThree class="w-4.5 h-4.5 text-gray-700" />
                  </div>
                </div>
              </div>
            </template>
          </ListElement>
        </hawk-modal-content>
        <hawk-modal-footer>
          <template #right>
            <div class="flex justify-end w-full">
              <HawkButton type="outlined" class="mr-4" @click="emits('close')">
                {{ $t('Cancel') }}
              </HawkButton>
              <ButtonElement
                :button-label="$t('Save')"
                :submits="true"
              />
            </div>
          </template>
        </hawk-modal-footer>
      </div>
    </Vueform>
  </hawk-modal-container>
</template>
